import React, { useState, useEffect, useCallback } from 'react';
import { generateUniqueDynamicBlock } from '../data/questionBank';
import { saveActivityToCloud } from '../config/firebase';
import InteractiveText from './InteractiveText';
import { getSpanishMeaning } from '../utils/dictionaryHelper';

const cleanOptionText = (rawOpt) => {
  if (!rawOpt) return '';
  return String(rawOpt).replace(/^[A-D][)\.]\s*/i, '').replace(/\s+/g, ' ').trim();
};

export default function ExamEngine({ grade, unit, currentUser, pinData, onFinishExam }) {
  // Examen de exactamente 15 preguntas dinámicas y aleatorias por intento
  const [questions] = useState(() => generateUniqueDynamicBlock(grade, unit, 15));

  // =========================================================================
  // 📋 ESTADO PRE-EXAMEN: ACEPTACIÓN DE PROTOCOLO Y CONTROL DE ENFOQUE
  // =========================================================================
  const [hasAcceptedRules, setHasAcceptedRules] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOpt, setSelectedOpt] = useState('');
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [userAnswerHistory, setUserAnswerHistory] = useState([]);

  // =========================================================================
  // 🛡️ SISTEMA DE CONTROL DE ENFOQUE Y ANTI-TRAMPAS (LIFECYCLE LISTENER)
  // =========================================================================
  const [exitCount, setExitCount] = useState(0);
  const [isExamPaused, setIsExamPaused] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [isExamVoided, setIsExamVoided] = useState(false);

  // Registrar intento anulado en Firebase Cloud y LocalStorage
  const recordVoidedExam = useCallback(() => {
    const totalCount = questions.length;
    const voidRecord = {
      id: currentUser ? (currentUser.code || currentUser.id) : 'EOH-ESTUDIANTE',
      name: currentUser ? (currentUser.username || currentUser.name) : 'Estudiante EOH',
      group: currentUser && currentUser.grade ? ('Grupo ' + currentUser.grade) : ('Grupo ' + grade + '01'),
      topic: 'Unidad ' + unit + ' (Grado ' + grade + '°)',
      type: 'Evaluación Anulada (Anti-Trampas / Salida de App)',
      score: '0 / ' + totalCount,
      pct: 0,
      status: 'Anulado (2 salidas no autorizadas)',
      violations: 2,
      xp: 0,
      date: new Date().toLocaleDateString('es-CO'),
      timestamp: new Date().toISOString()
    };

    saveActivityToCloud(voidRecord);

    const existing = JSON.parse(localStorage.getItem('motor_pedagogico_activities') || '[]');
    existing.unshift(voidRecord);
    localStorage.setItem('motor_pedagogico_activities', JSON.stringify(existing));
  }, [currentUser, grade, unit, questions.length]);

  // Listener del Ciclo de Vida de la App (Focus & Visibility Listener)
  useEffect(() => {
    // Solo activar la supervisión una vez el estudiante haya iniciado activamente la prueba
    if (!hasAcceptedRules || completed || isExamVoided) return;

    const handleFocusLoss = () => {
      if (!hasAcceptedRules || completed || isExamVoided) return;

      setExitCount((prev) => {
        const nextCount = prev + 1;

        if (nextCount === 1) {
          // ⚠️ PRIMERA SALIDA: Pausar examen, ocultar contenido y mostrar advertencia modal
          setIsExamPaused(true);
          setShowWarningModal(true);
        } else if (nextCount >= 2) {
          // 🚫 SEGUNDA SALIDA: Bloqueo definitivo e irreversible, anulación y registro en BD
          setIsExamPaused(true);
          setShowWarningModal(false);
          setIsExamVoided(true);
          recordVoidedExam();
        }

        return nextCount;
      });
    };

    const onVisibilityChange = () => {
      if (document.hidden || document.visibilityState === 'hidden') {
        handleFocusLoss();
      }
    };

    const onWindowBlur = () => {
      handleFocusLoss();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onWindowBlur);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [hasAcceptedRules, completed, isExamVoided, recordVoidedExam]);

  // Reanudar tras la primera advertencia
  const handleResumeAfterWarning = () => {
    setShowWarningModal(false);
    setIsExamPaused(false);
  };

  // Estado para diccionario emergente sin audio
  const [activeWordData, setActiveWordData] = useState(null);

  const handleWordClick = async (cleanWord) => {
    const data = await getSpanishMeaning(cleanWord);
    setActiveWordData(data);
  };

  const handleAnswerSubmit = () => {
    if (!selectedOpt || isExamPaused || isExamVoided) return;

    const currentQ = questions[currentIndex];
    const rawCorrect = currentQ.ans || currentQ.answer || '';
    const correctClean = cleanOptionText(rawCorrect);
    const userClean = cleanOptionText(selectedOpt);
    const expText = currentQ.exp || currentQ.explanation || 'Revisa la regla gramatical o vocabulario en la guía.';

    const isCorrect = userClean === correctClean;

    let explanationText = '';
    if (isCorrect) {
      explanationText = `¡Excelente trabajo! 🎯 Es la respuesta correcta.\n\n💡 Explicación: ${expText}`;
      setScore(prev => prev + 1);
      setFeedback({ type: 'success', text: explanationText });
    } else {
      explanationText = `Ánimo, la opción correcta era: "${correctClean}".\n\n💡 Explicación: ${expText}`;
      setFeedback({ type: 'error', text: explanationText });
    }

    const answerRecord = {
      qNum: currentIndex + 1,
      type: currentQ.type || 'Comprensión Lectora',
      question: currentQ.q,
      userAns: selectedOpt,
      correctAns: rawCorrect,
      isCorrect,
      explanation: expText
    };

    setUserAnswerHistory(prev => [...prev, answerRecord]);

    setTimeout(() => {
      setFeedback(null);
      setSelectedOpt('');
      if (currentIndex + 1 < questions.length) {
        setCurrentIndex(prev => prev + 1);
      } else {
        setCompleted(true);
        saveFinalExamScore([...userAnswerHistory, answerRecord]);
      }
    }, 2800);
  };

  const saveFinalExamScore = (historyLog) => {
    const finalScore = historyLog.filter(h => h.isCorrect).length;
    const totalCount = questions.length; // 15
    const pct = Math.round((finalScore / totalCount) * 100);
    const isApproved = pct >= 80;
    const statusLabel = isApproved ? 'Aprobado' : 'No aprobado';

    const wrongAnswers = historyLog.filter(h => !h.isCorrect);
    const topicsToImproveMap = new Map();
    wrongAnswers.forEach(item => {
      const topicTag = item.type || 'Vocabulario General';
      if (!topicsToImproveMap.has(topicTag)) {
        topicsToImproveMap.set(topicTag, []);
      }
      topicsToImproveMap.get(topicTag).push(item.explanation);
    });

    const topicsSummary = Array.from(topicsToImproveMap.entries()).map(([tag, exps]) => ({
      category: tag,
      details: exps.slice(0, 2)
    }));

    const examRecord = {
      id: currentUser ? (currentUser.code || currentUser.id) : 'EOH-ESTUDIANTE',
      name: currentUser ? (currentUser.username || currentUser.name) : 'Estudiante EOH',
      group: currentUser && currentUser.grade ? ('Grupo ' + currentUser.grade) : ('Grupo ' + grade + '01'),
      topic: 'Unidad ' + unit + ' (Grado ' + grade + '°)',
      type: 'Evaluación ICFES (PIN ' + (pinData?.pin || 'ACTIVO') + ')',
      score: finalScore + ' / ' + totalCount,
      pct: pct,
      status: statusLabel,
      topicsToImprove: topicsSummary,
      xp: finalScore * 10,
      violations: exitCount,
      date: new Date().toLocaleDateString('es-CO'),
      timestamp: new Date().toISOString()
    };

    saveActivityToCloud(examRecord);

    const existingActivities = JSON.parse(localStorage.getItem('motor_pedagogico_activities') || '[]');
    existingActivities.unshift(examRecord);
    localStorage.setItem('motor_pedagogico_activities', JSON.stringify(existingActivities));
  };

  // Calcular resultado final
  const pctFinal = Math.round((score / questions.length) * 100);
  const isApproved = pctFinal >= 80;
  const wrongList = userAnswerHistory.filter(h => !h.isCorrect);

  // =========================================================================
  // 1️⃣ PANTALLA PREVIA: AVISO Y COMPROMISO DE ENFOQUE ANTI-TRAMPAS
  // =========================================================================
  if (!hasAcceptedRules) {
    return (
      <div className="bg-slate-900 border-2 border-purple-500/60 p-6 md:p-10 rounded-3xl space-y-6 shadow-2xl animate-fadeIn text-left max-w-2xl mx-auto">
        
        {/* ENCABEZADO DE ADVERTENCIA PREVIA */}
        <div className="flex items-center gap-4 border-b border-slate-800 pb-4">
          <div className="w-14 h-14 bg-purple-950 border-2 border-purple-500 rounded-2xl flex items-center justify-center text-3xl shrink-0 shadow-lg">
            🛡️
          </div>
          <div>
            <span className="text-[10px] font-black uppercase tracking-widest text-amber-400 bg-amber-950 px-2.5 py-0.5 rounded-full border border-amber-500/40">
              Protocolo de Seguridad Académica
            </span>
            <h2 className="text-xl md:text-2xl font-black text-white mt-1">
              Normas de Enfoque y Supervisión
            </h2>
            <p className="text-xs text-slate-400">
              Lee atentamente las instrucciones antes de visualizar la primera pregunta.
            </p>
          </div>
        </div>

        {/* PUNTOS CLAVE DEL PROTOCOLO */}
        <div className="space-y-3 text-xs md:text-sm text-slate-200 leading-relaxed">
          
          <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 flex items-start gap-3">
            <span className="text-xl">📱</span>
            <div>
              <strong className="text-white block font-bold">1. Permanece dentro de la aplicación</strong>
              <span className="text-slate-400 text-xs">
                No cambies de pestaña, no minimices el navegador ni abras otras aplicaciones o traductores durante la prueba.
              </span>
            </div>
          </div>

          <div className="bg-amber-950/40 p-4 rounded-2xl border border-amber-500/40 flex items-start gap-3">
            <span className="text-xl">⚠️</span>
            <div>
              <strong className="text-amber-300 block font-bold">2. Primera salida = Advertencia y Pausa</strong>
              <span className="text-slate-300 text-xs">
                Si la aplicación detecta que sales por primera vez, el examen se pausará y el contenido de las preguntas se ocultará temporalmente.
              </span>
            </div>
          </div>

          <div className="bg-red-950/40 p-4 rounded-2xl border border-red-500/40 flex items-start gap-3">
            <span className="text-xl">🚫</span>
            <div>
              <strong className="text-red-300 block font-bold">3. Segunda salida = Bloqueo y Anulación</strong>
              <span className="text-slate-300 text-xs">
                Si sales por segunda vez, el examen quedará <strong>automáticamente anulado con calificación de 0%</strong> y se registrará en el sistema del profesor.
              </span>
            </div>
          </div>

          <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 flex items-start gap-3">
            <span className="text-xl">🎯</span>
            <div>
              <strong className="text-white block font-bold">4. Estructura y Criterio de Aprobación</strong>
              <span className="text-slate-400 text-xs">
                La prueba contiene <strong>15 preguntas aleatorias</strong> tipo ICFES/Saber. Se aprueba con el <strong>80% de aciertos (12/15)</strong>.
              </span>
            </div>
          </div>

        </div>

        {/* BOTÓN DE CONFIRMACIÓN */}
        <div className="pt-2">
          <button
            onClick={() => setHasAcceptedRules(true)}
            className="btn-3d btn-3d-emerald w-full py-4 text-xs md:text-sm font-black gap-2 shadow-2xl"
          >
            ✅ He leído las normas y acepto el protocolo → Iniciar Preguntas 🚀
          </button>
        </div>

      </div>
    );
  }

  // =========================================================================
  // 🚫 VISTA SI EL EXAMEN FUE ANULADO POR SEGUNDA SALIDA
  // =========================================================================
  if (isExamVoided) {
    return (
      <div className="bg-red-950/90 border-2 border-red-500 p-8 md:p-12 rounded-3xl space-y-6 shadow-2xl animate-fadeIn text-center text-white max-w-2xl mx-auto">
        <div className="w-20 h-20 bg-red-900/60 border-2 border-red-400 rounded-full flex items-center justify-center text-4xl mx-auto shadow-xl">
          🚫
        </div>

        <div className="space-y-2">
          <span className="text-[10px] font-black uppercase tracking-widest bg-red-900 text-red-200 border border-red-400 px-3 py-1 rounded-full">
            Infracción de Seguridad Anti-Trampas
          </span>
          <h2 className="text-2xl md:text-3xl font-black text-white">
            Examen Anulado Definitivamente
          </h2>
        </div>

        <div className="bg-slate-950/80 border border-red-500/40 p-6 rounded-2xl space-y-3 text-xs md:text-sm text-slate-300 leading-relaxed text-left">
          <p>
            <strong className="text-red-400">Motivo de la anulación:</strong> Se detectó que saliste de la aplicación o cambiaste de ventana en <strong className="text-white">dos ocasiones (2 salidas)</strong> durante la sesión del examen.
          </p>
          <p>
            Por normatividad institucional y control de enfoque, la prueba ha sido bloqueada irreversiblemente y calificada con <strong className="text-red-400">0% (Anulado)</strong>.
          </p>
          <div className="pt-2 border-t border-slate-800 text-[11px] text-slate-400 flex items-center gap-2">
            <span>🔒</span> Esta infracción fue registrada en la base de datos para la respectiva auditoría del docente.
          </div>
        </div>

        <div className="pt-4">
          <button
            onClick={() => {
              if (onFinishExam) onFinishExam();
            }}
            className="btn-3d btn-3d-slate px-8 py-3 text-xs font-black gap-2 shadow-2xl"
          >
            ← Volver al Menú Principal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-slate-100 relative animate-fadeIn">
      
      {/* ⚠️ MODAL DE ADVERTENCIA EN PANTALLA COMPLETA (PRIMERA SALIDA) */}
      {showWarningModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-lg flex items-center justify-center p-4 animate-fadeIn text-left">
          <div className="bg-slate-900 border-2 border-amber-500 rounded-3xl max-w-lg w-full p-6 md:p-8 space-y-6 shadow-2xl">
            
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-amber-950 border-2 border-amber-500 rounded-2xl flex items-center justify-center text-3xl shrink-0 shadow-lg">
                ⚠️
              </div>
              <div>
                <span className="text-[10px] font-black uppercase tracking-widest text-amber-400 bg-amber-950 px-2.5 py-0.5 rounded-full border border-amber-500/40">
                  Control de Enfoque — Advertencia (1 / 2)
                </span>
                <h3 className="text-lg md:text-xl font-black text-white mt-1">
                  Salida de la Aplicación Detectada
                </h3>
              </div>
            </div>

            <div className="bg-slate-950 p-4 rounded-2xl border border-amber-500/30 text-xs md:text-sm text-slate-200 leading-relaxed space-y-2">
              <p className="font-bold text-amber-300">
                Parece que saliste de la aplicación. Esta es una advertencia.
              </p>
              <p className="text-slate-300">
                Recuerda que no está permitido usar diccionarios u otras apps durante la prueba. <strong>Si sales nuevamente, el ejercicio se desactivará.</strong>
              </p>
            </div>

            <div className="pt-2">
              <button
                onClick={handleResumeAfterWarning}
                className="btn-3d btn-3d-amber w-full py-3.5 text-xs md:text-sm font-black gap-2 shadow-2xl"
              >
                ✅ Entendido, reanudar prueba 🚀
              </button>
            </div>

          </div>
        </div>
      )}

      {/* BARRA DE DATOS DE EVALUACIÓN OFICIAL */}
      <div className="bg-purple-950/80 border border-purple-500/40 p-4 rounded-2xl flex flex-wrap items-center justify-between gap-3 shadow-lg">
        <div>
          <span className="text-[10px] font-extrabold uppercase tracking-widest text-purple-300 bg-purple-900 px-2 py-0.5 rounded-md">
            Prueba Automatizada ICFES / Saber (15 Preguntas)
          </span>
          <h3 className="text-base font-extrabold text-white mt-1 flex items-center gap-2">
            <span>📝</span> Evaluación Grado {grade}° - Unidad {unit}
          </h3>
        </div>

        <div className="text-right text-xs space-y-0.5">
          <span className="text-slate-300 block">Estudiante: <strong className="text-white">{currentUser?.username || currentUser?.name || 'Estudiante'}</strong></span>
          <span className="text-purple-300 font-mono font-bold text-[11px] block">PIN: {pinData?.pin || 'ACTIVO'}</span>
          <span className="text-[10px] font-bold text-slate-400">
            🛡️ Enfoque: {exitCount === 0 ? '🟢 Óptimo (0/2)' : '🟡 1 Advertencia (1/2)'}
          </span>
        </div>
      </div>

      {!completed ? (
        <div className="bg-slate-900/90 border border-slate-700 p-6 rounded-3xl space-y-6 shadow-2xl">
          
          {/* BARRA DE PROGRESO */}
          <div className="space-y-2">
            <div className="flex justify-between items-center text-xs font-bold text-slate-300">
              <span className="flex items-center gap-2">
                <span className="bg-purple-950 text-purple-300 border border-purple-500/40 px-2 py-0.5 rounded-md text-[10px]">
                  {questions[currentIndex]?.type || 'ICFES'}
                </span>
                Pregunta {currentIndex + 1} de {questions.length}
              </span>
              <span className="text-purple-400">Progreso: {Math.round(((currentIndex + 1) / questions.length) * 100)}%</span>
            </div>

            <div className="w-full bg-slate-950 h-2.5 rounded-full overflow-hidden border border-slate-800">
              <div 
                className="h-full bg-gradient-to-r from-purple-600 to-indigo-500 transition-all duration-300"
                style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
              />
            </div>
          </div>

          {/* PREGUNTA ACTUAL (OCULTA SI ESTÁ PAUSADA POR ADVERTENCIA) */}
          {isExamPaused ? (
            <div className="p-12 text-center text-slate-400 space-y-3">
              <div className="text-4xl animate-pulse">⏸️</div>
              <p className="text-sm font-bold text-white">Prueba en pausa por control de enfoque...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-base md:text-lg font-extrabold text-white leading-relaxed whitespace-pre-line">
                <InteractiveText 
                  text={questions[currentIndex]?.q} 
                  onWordClick={handleWordClick} 
                />
              </h3>

              {/* OPCIONES DE RESPUESTA A, B, C, D */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {questions[currentIndex]?.opts.map((optRaw, idx) => {
                  const cleanText = cleanOptionText(optRaw);
                  const letter = String.fromCharCode(65 + idx); // 'A', 'B', 'C', 'D'
                  const fullOptionLabel = `${letter}) ${cleanText}`;
                  const isSelected = cleanOptionText(selectedOpt) === cleanText;

                  return (
                    <button
                      key={idx}
                      onClick={() => setSelectedOpt(fullOptionLabel)}
                      className={`p-3.5 rounded-2xl text-xs font-bold text-left transition cursor-pointer border flex items-center gap-2 ${
                        isSelected 
                          ? 'bg-purple-600 border-purple-400 text-white shadow-lg scale-[1.02]' 
                          : 'bg-slate-950/80 border-slate-800 text-slate-200 hover:bg-slate-800'
                      }`}
                    >
                      <span className={`font-extrabold shrink-0 ${isSelected ? 'text-white' : 'text-purple-400'}`}>
                        {letter})
                      </span>
                      <InteractiveText 
                        text={cleanText} 
                        onWordClick={handleWordClick} 
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* FEEDBACK DE RESPUESTA */}
          {feedback && (
            <div className={`p-4 rounded-2xl text-xs font-bold text-left border animate-fadeIn leading-relaxed whitespace-pre-line ${
              feedback.type === 'success' ? 'bg-emerald-950/90 border-emerald-500/60 text-emerald-200' : 'bg-amber-950/90 border-amber-500/60 text-amber-200'
            }`}>
              {feedback.text}
            </div>
          )}

          {/* BOTÓN SUBMIT */}
          <div className="flex justify-end pt-2">
            <button
              onClick={handleAnswerSubmit}
              disabled={!selectedOpt || isExamPaused}
              className="bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white font-bold px-6 py-2.5 rounded-xl text-xs shadow-lg transition cursor-pointer"
            >
              {currentIndex + 1 === questions.length ? 'Finalizar Examen y Calcular Estado 🚀' : 'Comprobar Respuesta 🚀'}
            </button>
          </div>

        </div>
      ) : (
        /* RESULTADOS FINALES Y DIAGNÓSTICO DE APRENDIZAJE */
        <div className="bg-slate-900/95 border border-purple-500/60 p-6 md:p-8 rounded-3xl space-y-6 shadow-2xl animate-fadeIn text-center">
          
          {/* ETIQUETA OFICIAL DE ESTADO: "Aprobado" O "No aprobado" */}
          <div className="space-y-3">
            <div className="text-4xl">{isApproved ? '🏆' : '💪'}</div>
            
            <div className="inline-block">
              <span className={`px-5 py-1.5 rounded-full text-base font-black uppercase tracking-wider border shadow-xl ${
                isApproved 
                  ? 'bg-emerald-950 text-emerald-300 border-emerald-500' 
                  : 'bg-red-950 text-red-300 border-red-500'
              }`}>
                Estado Final: {isApproved ? 'Aprobado' : 'No aprobado'}
              </span>
            </div>

            <p className="text-xs text-slate-400">
              Calificación mínima requerida para aprobar: <strong className="text-white">80% de aciertos</strong> (12 / 15 preguntas).
            </p>
          </div>

          {/* RESUMEN DE PUNTAJE */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-lg mx-auto text-left">
            <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800">
              <div className="text-[10px] uppercase font-bold text-slate-500">Aciertos</div>
              <div className="text-2xl font-black text-white">{score} / {questions.length}</div>
            </div>

            <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800">
              <div className="text-[10px] uppercase font-bold text-slate-500">Porcentaje</div>
              <div className="text-2xl font-black text-purple-400">{pctFinal}%</div>
            </div>

            <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800">
              <div className="text-[10px] uppercase font-bold text-slate-500">Puntos XP</div>
              <div className="text-2xl font-black text-emerald-400">+{score * 10} XP</div>
            </div>
          </div>

          {/* LISTA DE TEMAS A MEJORAR SI HUBO ERRORES */}
          {wrongList.length > 0 && (
            <div className="bg-slate-950 p-5 rounded-2xl border border-amber-500/30 text-left space-y-3">
              <h4 className="text-xs font-black uppercase text-amber-400 flex items-center gap-2">
                <span>💡</span> Temas y Vocabulario a Reforzar para Próximos Intentos:
              </h4>
              <ul className="space-y-2 text-xs text-slate-300">
                {wrongList.slice(0, 4).map((w, idx) => (
                  <li key={idx} className="border-b border-slate-800/80 pb-2">
                    <span className="font-bold text-white">• Pregunta {w.qNum} ({w.type}):</span> {w.explanation}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* BOTÓN FINALIZAR */}
          <div className="pt-4">
            <button
              onClick={() => {
                if (onFinishExam) onFinishExam();
              }}
              className="btn-3d btn-3d-emerald px-8 py-3 text-xs font-black gap-2 shadow-2xl"
            >
              📊 Ver Mi Historial de Notas y Continuar 🚀
            </button>
          </div>

        </div>
      )}

      {/* MODAL TRADUCTOR EMERGENTE */}
      {activeWordData && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn text-left">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-sm w-full p-6 space-y-4 shadow-2xl">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <span className="text-[10px] font-bold uppercase text-purple-400">Diccionario Olayista</span>
                <h4 className="text-lg font-black text-white">{activeWordData.word}</h4>
              </div>
              <button
                onClick={() => setActiveWordData(null)}
                className="text-slate-400 hover:text-white font-bold text-lg bg-slate-800 w-7 h-7 rounded-full flex items-center justify-center transition cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-2 text-xs text-slate-300">
              <div className="bg-slate-950 p-3 rounded-xl border border-slate-800 space-y-1">
                <div className="text-[10px] uppercase font-bold text-slate-500">Significado:</div>
                <div className="text-white font-bold text-sm">{activeWordData.spanish || 'Término de la unidad'}</div>
              </div>
            </div>

            <button
              onClick={() => setActiveWordData(null)}
              className="w-full btn-3d btn-3d-blue py-2 text-xs font-bold"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
